<script setup>
import { computed, reactive, ref, watch } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  invalidNumbers: {
    type: Array,
    default: () => [],
  },
  isRevalidating: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'revalidate', 'remove-all']);

// Local editable copy to avoid mutating parent data directly
const state = reactive({
  rows: [],
});

// Mensagem de sucesso após revalidação
const successMessage = ref('');

const totalInvalid = computed(() => state.rows.length);

const editedCount = computed(
  () =>
    state.rows.filter(
      row => row.editedNumber && row.editedNumber.trim() !== row.number
    ).length
);

const resetRows = data => {
  state.rows =
    (data || []).map(item => ({
      // Suporta tanto estrutura antiga (number) quanto nova (phone/phone_number de recipients)
      number: item.number || item.phone || item.phone_number || '',
      exists: item.exists ?? false,
      name: item.name || '',
      jid: item.jid || '',
      editedNumber: item.number || item.phone || item.phone_number || '',
    })) || [];
  // Limpa mensagem de sucesso ao atualizar lista
  successMessage.value = '';
};

watch(
  () => props.invalidNumbers,
  newVal => resetRows(newVal),
  { immediate: true, deep: true }
);

// Confirmar: re-valida os números editados
const handleConfirm = () => {
  const payload = {
    edits: [],
    ignored: [],
  };

  state.rows.forEach(row => {
    const updated = row.editedNumber ? row.editedNumber.trim() : '';
    if (updated && updated !== row.number) {
      payload.edits.push({
        original: row.number,
        updated,
      });
    }
  });

  emit('revalidate', payload);
};

// Remover todos e confirmar
const handleRemoveAll = () => {
  const allNumbers = state.rows.map(row => row.number);
  emit('remove-all', allNumbers);
};

// Método para mostrar mensagem de sucesso (chamado pelo parent)
const showSuccessMessage = validCount => {
  if (validCount > 0) {
    successMessage.value = `${validCount} número(s) agora válido(s) e adicionado(s) à lista!`;
    setTimeout(() => {
      successMessage.value = '';
    }, 5000);
  }
};

defineExpose({ showSuccessMessage });
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black75 dark:bg-n-alpha-black75 backdrop-blur-sm"
  >
    <div
      class="relative w-full max-w-3xl max-h-screen overflow-hidden bg-n-solid-1 dark:bg-n-solid-1 border border-n-slate-6 dark:border-n-slate-6 shadow-2xl rounded-xl flex flex-col animate-in fade-in-0 zoom-in-95 duration-300"
    >
      <!-- Header -->
      <div
        class="flex justify-between items-center px-5 py-4 border-b border-n-slate-6 dark:border-n-slate-6 bg-n-slate-2 dark:bg-n-slate-2 rounded-t-xl"
      >
        <div class="flex items-center gap-3">
          <div
            class="w-10 h-10 bg-gradient-to-br from-n-amber-9 to-n-amber-10 dark:from-n-amber-9 dark:to-n-amber-10 rounded-lg flex items-center justify-center shadow-md"
          >
            <i class="i-lucide-phone-off text-white text-base" />
          </div>
          <div>
            <h3 class="text-xl font-bold text-n-slate-12 dark:text-n-slate-12">
              Números não encontrados no WhatsApp
            </h3>
            <p class="text-sm text-n-slate-11 dark:text-n-slate-11 font-medium">
              Corrija os números ou remova da lista de envio
            </p>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        <!-- Mensagem de sucesso -->
        <div
          v-if="successMessage"
          class="bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border border-green-300 dark:border-green-700 rounded-lg p-4"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center shadow-sm"
            >
              <i class="i-lucide-check text-white text-sm" />
            </div>
            <p class="text-sm font-medium text-green-700 dark:text-green-300">
              {{ successMessage }}
            </p>
          </div>
        </div>

        <div
          class="bg-gradient-to-r from-n-amber-2 to-n-amber-3 dark:from-n-amber-2 dark:to-n-amber-3 border border-n-amber-6 dark:border-n-amber-6 rounded-lg p-4"
        >
          <div class="flex items-start gap-3">
            <div
              class="w-8 h-8 bg-gradient-to-br from-n-amber-9 to-n-amber-10 dark:from-n-amber-9 dark:to-n-amber-10 rounded-lg flex items-center justify-center shadow-sm"
            >
              <i class="i-lucide-alert-triangle text-white text-sm" />
            </div>
            <div class="space-y-1 text-sm text-n-amber-11 dark:text-n-amber-11">
              <p>
                <strong>{{ totalInvalid }}</strong> número(s) não existem no
                WhatsApp.
              </p>
              <p>Corrija os números abaixo ou remova todos da lista.</p>
            </div>
          </div>
        </div>

        <div
          class="bg-n-solid-1 border border-n-slate-6 rounded-lg divide-y divide-n-slate-6"
        >
          <div
            v-for="(row, index) in state.rows"
            :key="row.number + index"
            class="p-4 flex flex-col gap-3"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-9 h-9 rounded-lg bg-n-amber-3 flex items-center justify-center text-n-amber-11 flex-shrink-0"
              >
                <i class="i-lucide-alert-octagon" />
              </div>
              <div class="flex-1">
                <p
                  v-if="row.name"
                  class="text-base font-semibold text-n-slate-12"
                >
                  {{ row.name }}
                </p>
                <p class="text-sm text-n-slate-11">
                  {{ row.number || 'Sem número' }}
                </p>
              </div>
            </div>

            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-12">
                Corrigir número
              </label>
              <input
                v-model="row.editedNumber"
                type="text"
                class="w-full px-3 py-2 rounded-lg border border-n-slate-6 bg-n-solid-1 text-n-slate-12 placeholder:text-n-slate-10"
                placeholder="Digite o número corrigido"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex justify-between items-center px-5 py-4 border-t border-n-slate-6 dark:border-n-slate-6 bg-n-slate-2 dark:bg-n-slate-2 rounded-b-xl"
      >
        <div class="text-sm text-n-slate-11">
          <span class="font-semibold text-n-slate-12">{{ editedCount }}</span>
          número(s) editado(s)
        </div>
        <div class="flex gap-3">
          <Button
            label="Remover todos"
            variant="outline"
            color="ruby"
            :disabled="isRevalidating"
            @click="handleRemoveAll"
          />
          <Button
            :label="isRevalidating ? 'Validando...' : 'Verificar e confirmar'"
            color="blue"
            :disabled="isRevalidating"
            @click="handleConfirm"
          />
        </div>
      </div>
    </div>
  </div>
</template>
